import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
	Box,
	Tabs,
	Tab,
	Typography,
	Paper,
	FormControl,
	Select,
	MenuItem,
	CircularProgress,
	Grid2,
	Alert,
	Divider,
	Button,
	Snackbar,
	IconButton,
	Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import { useLoaderData, useParams } from 'react-router';
import { authedApiFetch } from '../../../utils/Api';
import UnifiedTabPanel from '../components/UnifiedTabPanel';
import DealStoryView from '../components/DealStory/DealStoryView';
import DealDrawer from '../components/DealDrawer';
import { formatCurrency } from '../../../utils/utils';
import useDealStore from '../stores/dealStore';
import { formatDateTime } from '../../../utils/utils';

export const dealDetailLoader = async ({ params }) => {
	const { tenantId } = params;

	try {
		// Fetch only categories - deal data will be loaded by the Zustand store
		const categoriesResponse = await authedApiFetch({
			endpoint: `/tenants/${tenantId}/config_lists?list_type=categories`,
			method: 'GET',
		});

		// Extract category items from the response
		let categoryItems = [];
		if (
			Array.isArray(categoriesResponse) &&
			categoriesResponse.length > 0 &&
			categoriesResponse[0].config_list_items
		) {
			categoryItems = categoriesResponse[0].config_list_items;
		}

		return {
			categoryItems,
			loading: false,
		};
	} catch (error) {
		console.error('Error loading categories:', error);
		return {
			categoryItems: [],
			loading: false,
			error: 'Failed to load categories: ' + error.message,
		};
	}
};

const DealDetail = () => {
	const { categoryItems, error: loaderError } = useLoaderData();
	const { tenantId, dealId } = useParams();
	const [activeTab, setActiveTab] = useState(0);
	// State for the edit drawer
	const [drawerOpen, setDrawerOpen] = useState(false);

	const [notification, setNotification] = useState({
		open: false,
		message: '',
		severity: 'success',
		autoHideDuration: 3000,
	});

	const handleCloseNotification = () => {
		setNotification({ ...notification, open: false });
	};

	// Get deal data and attributes from Zustand store
	const {
		deal: dealData,
		objectAttributes,
		objectMetrics,
		objectFacts,
		objectInsights,
		prompts,
		sourceMonitors,
		loading,
		error: storeError,
		fetchAllDealData,
		updateDeal,
	} = useDealStore();

	const navigate = useNavigate();

	// Add state for category filtering
	const [selectedCategory, setSelectedCategory] = useState('all');

	// Load deal data and attributes from the store when component mounts or params change
	useEffect(() => {
		if (dealId && tenantId) {
			fetchAllDealData(tenantId, dealId);
		}
	}, [dealId, tenantId, fetchAllDealData]);

	// Combine errors from loader and store
	const error = loaderError || storeError;

	// Handle opening and closing the edit drawer
	const handleOpenDrawer = () => setDrawerOpen(true);
	const handleCloseDrawer = () => setDrawerOpen(false);

	// Handle saving changes from the drawer
	const handleSave = async (updatedDealData) => {
		try {
			// The updateDeal function will update the store and return the updated deal
			await updateDeal(tenantId, dealId, updatedDealData);
			// Additional UI updates can be added here if needed
		} catch (error) {
			console.error('Error updating deal:', error);
			setNotification({
				open: true,
				message: 'Unable to update the deal. Please try again later.',
				severity: 'error',
				autoHideDuration: 6000,
			});
		}
	};

	// Handle tab change
	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	// New handler for category filter change
	const handleCategoryChange = (event) => {
		setSelectedCategory(event.target.value);
	};

	// Define fixed tabs (excluding category tabs)
	const fixedTabs = [
		{ key: 'summary', name: 'Executive Summary', type: 'summary' },
		{ key: 'information', name: 'Information', type: 'information' },
		{ key: 'sources', name: 'Sources', type: 'sources' },
		{ key: 'tasks', name: 'Tasks', type: 'tasks' },
		{ key: 'team', name: 'Team', type: 'team' },
		{ key: 'monitors', name: 'Monitors', type: 'monitors' },
		{ key: 'interactions', name: 'Interactions', type: 'interactions' },
	];

	// Define all tabs - we'll keep this for now
	const allTabs = [...fixedTabs];

	// Current active tab
	const currentTab = allTabs[activeTab];

	// Get data for current tab
	const getTabData = () => {
		if (!dealData) return null;

		if (!currentTab) return null;

		switch (currentTab.type) {
			case 'summary':
				return { dealId: dealId, tenantId: tenantId };
			case 'information':
				// Get all attributes/metrics/facts/insights
				// If selectedCategory is 'all', show everything
				// Otherwise, filter by the selected category
				return {
					attributes: (objectAttributes || [])
						.filter(
							(attr) =>
								selectedCategory === 'all' || attr.category === selectedCategory
						)
						.map((attr) => ({ ...attr, useNewDisplay: true })),
					metrics: (objectMetrics || [])
						.filter(
							(metric) =>
								selectedCategory === 'all' ||
								metric.category === selectedCategory
						)
						.map((metric) => ({ ...metric, useNewDisplay: true })),
					facts: (objectFacts || [])
						.filter(
							(fact) =>
								selectedCategory === 'all' || fact.category === selectedCategory
						)
						.map((fact) => ({ ...fact, useNewDisplay: true })),
					insights: (objectInsights || [])
						.filter(
							(insight) =>
								selectedCategory === 'all' ||
								insight.category === selectedCategory
						)
						.map((insight) => ({ ...insight, useNewDisplay: true })),
				};
			case 'tasks':
				return { tasks: dealData.tasks || [] };
			case 'team':
				return { teamMembers: dealData.team_members || [] };
			case 'monitors':
				return { monitors: sourceMonitors || [] };
			case 'interactions':
				return { interactions: dealData.interactions || [] };
			case 'sources': {
				// Helper function to determine the appropriate type for icons
				const determineType = (category) => {
					// Map backend categories/source_types to frontend types
					if (!category) return 'internal';

					const lowerCategory = String(category).toLowerCase();

					if (
						lowerCategory.includes('document') ||
						lowerCategory.includes('pdf') ||
						lowerCategory.includes('excel') ||
						lowerCategory.includes('word')
					) {
						return 'document';
					} else if (
						lowerCategory.includes('database') ||
						lowerCategory.includes('subscription') ||
						lowerCategory.includes('api')
					) {
						return 'subscription';
					} else {
						return 'internal'; // Default type
					}
				};

				// Map source_monitors to the expected structure and combine with static entries
				return {
					sources: [
						...(sourceMonitors || []).map((monitor) => ({
							id: monitor.id,
							name: monitor.name || 'Source Document', // Use description as name or fallback
							type: determineType(monitor.category || monitor.source_type),
							url: monitor.source_url || monitor.reference || '#', // Use source_url or reference
							format: monitor.category, // Use category as format if available
						})),
					],
					promptLibrary: prompts,
				};
			}
			default:
				return null;
		}
	};

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Alert severity='error' sx={{ mb: 2 }}>
					{error}
				</Alert>
			</Box>
		);
	}

	if (loading.deal || !dealData) {
		return (
			<Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
				<CircularProgress />
			</Box>
		);
	}

	// Format metric values for display
	// TODO: use this and remove underscore, or remove it
	const _formatMetricValue = (metric) => {
		if (!metric.value && metric.value !== 0) return '—';

		if (metric.value_type === 'currency') {
			const numValue = parseFloat(metric.value);
			if (numValue >= 1000000) {
				return `$${(numValue / 1000000).toFixed(1)}M`;
			} else if (numValue >= 1000) {
				return `$${(numValue / 1000).toFixed(0)}K`;
			}
			return `$${numValue.toLocaleString()}`;
		}

		if (metric.value_type === 'percentage') {
			return `${metric.value}%`;
		}

		if (metric.value_type === 'count') {
			return metric.value.toLocaleString();
		}

		// Default case
		return String(metric.value);
	};

	return (
		<Box sx={{ width: '100%', p: 2 }}>
			{error && (
				<Alert severity='error' sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{dealData && (
				<>
					<Box sx={{ mb: 3 }}>
						<Grid2 container spacing={2}>
							<Grid2 xs={12} sx={{ width: '100%' }}>
								<Paper
									sx={{
										p: 2,
										mb: 1,
										borderRadius: 2,
										width: '100%',
									}}
								>
									{/* Header with Deal Name and Edit Button */}
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											mb: 2,
										}}
									>
										<Stack direction='row' alignItems='center' spacing={1}>
											<IconButton
												onClick={() =>
													navigate(`/tenants/${tenantId}/apps/investing/deals`)
												}
												size='small'
												color='primary'
												aria-label='back'
											>
												<ArrowBackIosRoundedIcon fontSize='small' />
											</IconButton>
											<Typography variant='h4' component='h1'>
												{dealData.name}
											</Typography>
										</Stack>
										<Button
											variant='contained'
											color='primary'
											startIcon={<EditIcon />}
											onClick={handleOpenDrawer}
										>
											Edit Deal
										</Button>
									</Box>
									<Divider sx={{ mb: 2 }} />

									<Grid2 container spacing={2} columnSpacing={8} sx={{ mb: 3 }}>
										{/* Column 1 */}
										<Grid2 xs={12} md={4}>
											<Box sx={{ display: 'flex', mb: 1 }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Stage:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{dealData.stage_name || dealData.stage || 'N/A'}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', mb: 1 }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Company:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{dealData.company_info?.name || 'N/A'}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex' }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Created:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{formatDateTime(dealData.created_at) || 'N/A'}
												</Typography>
											</Box>
										</Grid2>

										{/* Column 2 */}
										<Grid2 xs={12} md={4}>
											<Box sx={{ display: 'flex', mb: 1 }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Deal Size:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{dealData.deal_size
														? formatCurrency(
																dealData.deal_size,
																dealData.currency_name
															)
														: 'N/A'}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', mb: 1 }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Fund:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{dealData.fund_info?.name || 'N/A'}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex' }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Updated:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{formatDateTime(dealData.updated_at) || 'N/A'}
												</Typography>
											</Box>
										</Grid2>

										{/* Column 3 */}
										<Grid2 xs={12} md={4}>
											<Box sx={{ display: 'flex' }}>
												<Typography
													variant='body2'
													sx={{ color: 'text.secondary', width: 80 }}
												>
													Owner:
												</Typography>
												<Typography
													variant='body2'
													sx={{ fontWeight: 'medium', ml: 1 }}
												>
													{dealData.owner_name || dealData.owner_id || 'N/A'}
												</Typography>
											</Box>
										</Grid2>
									</Grid2>

									{/* Company Overview */}
									{dealData.company_info?.company_overview && (
										<>
											<Divider sx={{ my: 1 }} />
											<Typography variant='body2' sx={{ fontWeight: 'medium' }}>
												Company Overview
											</Typography>
											<Typography variant='body2' color='text.secondary'>
												{dealData.company_info.company_overview}
											</Typography>
										</>
									)}
								</Paper>
							</Grid2>
						</Grid2>
					</Box>

					<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
						<Tabs
							value={activeTab}
							onChange={handleTabChange}
							aria-label='deal tabs'
							variant='scrollable'
							scrollButtons='auto'
						>
							{allTabs.map((tab, index) => (
								<Tab
									key={tab.key}
									label={tab.name}
									id={`tab-${index}`}
									aria-controls={`tabpanel-${index}`}
								/>
							))}
						</Tabs>
					</Box>

					{/* Add category filter dropdown for Information tab */}
					{currentTab && currentTab.type === 'information' && (
						<Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
							<Typography variant='body1' sx={{ mr: 2 }}>
								Filter by Category:
							</Typography>
							<FormControl variant='outlined' size='small'>
								<Select
									value={selectedCategory}
									onChange={handleCategoryChange}
									displayEmpty
								>
									<MenuItem value='all'>All Categories</MenuItem>
									{(categoryItems || []).map((category) => (
										<MenuItem key={category.id} value={category.id}>
											{category.display_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					)}

					{/* Tab content */}
					<Box sx={{ p: 1 }}>
						{currentTab &&
							(currentTab.type === 'summary' ? (
								<DealStoryView dealId={dealId} tenantId={tenantId} />
							) : (
								<UnifiedTabPanel
									tabType={currentTab.type}
									tabKey={currentTab.key}
									data={getTabData()}
									title={currentTab.name}
									categoryItems={categoryItems}
								/>
							))}
					</Box>
				</>
			)}

			{/* Deal Drawer for editing */}
			<DealDrawer
				open={drawerOpen}
				onClose={handleCloseDrawer}
				onSave={handleSave}
				tenantId={tenantId}
				editMode={true}
				dealData={dealData}
			/>

			<Snackbar
				open={notification.open}
				onClose={handleCloseNotification}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={notification.autoHideDuration}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant='filled'
					sx={{ width: '100%' }}
				>
					{notification.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default DealDetail;
